import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'
import type { RootState } from './index'
import { RfqParams, UserConfigInfo } from '../api/generated'
import { GuidedOfferingClientState } from '../types/GuidedOffering'
import { ScaniaAdobeTrackingFlowInfo } from '../utils/adobeAnalytics'
import {
  ExtendedCheckStatus,
  GuidedViewPageType,
  ImageSeriesFrame,
  LoginStatus,
  PageViewTrackingEventPayload,
  ReadMoreData,
  SessionError,
  SmartDashCampaignMode,
} from './types'
import { StartupDataBundle } from '../api/startup'
import { GuidedOfferingClientStateStatus } from '../types/GuidedOffering/GuidedOfferingClientState'

export interface SessionState {
  account: { isloggedin: boolean; userMail: string }
  bodyBuildToggled: boolean
  counter: number
  error: SessionError
  extendedCheckState: ExtendedCheckStatus
  garage: { list: UserConfigInfo[] | null }
  guidedViewPage: GuidedViewPageType | null
  guidedViewLoadTime: number | null
  guidedViewFirstTimeAllView: boolean
  guidedViewPageDescriptionHidden: boolean
  summaryOrRfqOpenedFromPage: GuidedViewPageType | null

  /**
   * Should be populated by the response object from goWizadsStart, this data is
   * only available on that response object, not on goWizardContinue response
   * objects.
   */
  goEtelLastUpdated: string | null // Samma som finns på GuidedOfferingClientState?

  /**
   * The current Guided Offering state, should first be set by storing the
   * response from goWizardStart and then overwritten by response objects from
   * goWizardContinue and finally set to null again when the Guided Offering
   * sequence is completed.
   *
   * Should be null for pure Factory Model markets.
   */
  guidedOfferingState: GuidedOfferingClientState | null

  marketDenomination: string | null
  pendingUserTrackingGoSelection: ScaniaAdobeTrackingFlowInfo | null
  preload: {
    startHidePreloader: boolean
    preloaderIsHidden: boolean
  }
  readmore: ReadMoreData | null
  rfqInput: RfqParams | null
  rfqOrEysButtonEnabled: boolean
  isLoadedTruck: boolean
  seriesFrame: ImageSeriesFrame
  sessionId: string | null
  showNewsBanner: boolean
  showPanoramaViewer: boolean
  showFullPageViewer: boolean
  smartDashCampaignMode: SmartDashCampaignMode
  startupDataBundle: StartupDataBundle | null // Null before session initialization.
  userSelectionTimestampReduxCompatible: number | null
  unavailableImageFrames: ImageSeriesFrame[]
}

var initialState: SessionState = {
  account: { isloggedin: false, userMail: '' },
  bodyBuildToggled: true,
  counter: 0,
  error: {
    errorLevel: 0,
    friendlyErrorMessage: '',
    errorMessage: null,
  },
  extendedCheckState: ExtendedCheckStatus.None,
  garage: { list: null },
  guidedViewPage: null,
  goEtelLastUpdated: null,
  guidedOfferingState: null,
  guidedViewLoadTime: null,
  guidedViewFirstTimeAllView: true,
  guidedViewPageDescriptionHidden: false,
  summaryOrRfqOpenedFromPage: null,
  marketDenomination: null,
  pendingUserTrackingGoSelection: null,
  preload: {
    startHidePreloader: false,
    preloaderIsHidden: false,
  },
  readmore: null,
  rfqInput: null,
  rfqOrEysButtonEnabled: true,
  sessionId: null,
  startupDataBundle: null,
  seriesFrame: {
    seriesId: 'EXTERIOR',
    frameId: 2,
  },
  isLoadedTruck: false,
  showPanoramaViewer: false,
  showFullPageViewer: false,
  showNewsBanner: true,
  smartDashCampaignMode: SmartDashCampaignMode.None,
  unavailableImageFrames: [],
  userSelectionTimestampReduxCompatible: null,
}

if (window.tempStartupCriticalState) {
  initialState = {
    ...initialState,
    ...window.tempStartupCriticalState.sessionData,
  }
}

export const sessionDataSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    deleteTruckFromGarageList: (state, action: PayloadAction<number>) => {
      if (!state.garage.list) {
        console.error('Expected garage.list to be defined.')
      } else {
        const updatedList = state.garage.list.filter(
          (truck: UserConfigInfo) => truck.id !== action.payload,
        )
        state.garage = { ...state.garage, list: updatedList }
      }
    },
    setRfqInput: (state, action: PayloadAction<RfqParams | null>) => {
      state.rfqInput = action.payload
    },
    setRfqOrEysButtonEnabled: (state, action: PayloadAction<boolean>) => {
      state.rfqOrEysButtonEnabled = action.payload
    },
    setError: (state, action: PayloadAction<SessionError>) => {
      console.error(action.payload.errorMessage)
      if (state.error.errorLevel <= 2) {
        state.error = { ...state.error, ...action.payload }
      }
    },
    setGarageList: (state, action: PayloadAction<UserConfigInfo[] | null>) => {
      state.garage = { ...state.garage, list: action.payload }
    },
    // BuildModePage should set this to null when initializing itself.
    setGuidedOfferingState: (
      state,
      action: PayloadAction<GuidedOfferingClientState | null>,
    ) => {
      state.guidedOfferingState = action.payload
      const goEtelLastUpdated = action.payload?.etelLastUpdated
      if (goEtelLastUpdated) {
        state.goEtelLastUpdated = goEtelLastUpdated
      }
    },
    setGuidedOfferingStateStatus: (
      state,
      action: PayloadAction<GuidedOfferingClientStateStatus>,
    ) => {
      const oldState = state.guidedOfferingState
      if (!oldState) {
        return
      }
      const newState: GuidedOfferingClientState = { ...oldState }
      newState.status = action.payload
      setGuidedOfferingState(newState)
    },
    setInitState: (state, action: PayloadAction<StartupDataBundle>) => {
      if (action.payload.userConfigs !== null) {
        // This can happen when pressing the New button to restart the
        // application while logged in.
        state.garage = {
          ...state.garage,
          list: action.payload.userConfigs,
        }
        state.account = { isloggedin: true, userMail: '' }
      }
      state.startupDataBundle = action.payload
    },
    loggedInStatus: (state, action: PayloadAction<LoginStatus>) => {
      state.account = { ...state.account, ...action.payload }
    },

    /**
     * Needed to allow the destination page to pick up this information and include
     * it in the pageView event.
     */
    setPendingUserTrackingPageNavigation: (
      state,
      action: PayloadAction<ScaniaAdobeTrackingFlowInfo | null>,
    ) => {
      state.pendingUserTrackingGoSelection = action.payload
    },
    pushPageViewTrackingEvent: (
      state,
      action: PayloadAction<PageViewTrackingEventPayload>,
    ) => {},
    setSessionId: (state, action: PayloadAction<string | null>) => {
      state.sessionId = action.payload
    },
    startHideInitialLoadingScreen: (state) => {
      if (
        !state.preload.preloaderIsHidden &&
        !state.preload.startHidePreloader
      ) {
        state.preload = { ...state.preload, startHidePreloader: true }
      }
    },
    setLoadingScreenIsHidden: (state) => {
      state.preload = { ...state.preload, preloaderIsHidden: true }
    },
    setExtendedCheckState: (
      state,
      action: PayloadAction<ExtendedCheckStatus>,
    ) => {
      state.extendedCheckState = action.payload
    },
    setMarketDenomination: (state, action: PayloadAction<string | null>) => {
      state.marketDenomination = action.payload
    },
    setSeriesFrame: (state, action: PayloadAction<ImageSeriesFrame>) => {
      state.seriesFrame = action.payload
    },
    setIsLoadedTruck: (state, action: PayloadAction<boolean>) => {
      state.isLoadedTruck = action.payload
    },
    setShowFullPageViewer: (state, action: PayloadAction<boolean>) => {
      state.showFullPageViewer = action.payload
    },
    setShowPanoramaViewer: (state, action: PayloadAction<boolean>) => {
      state.showPanoramaViewer = action.payload
    },
    setShowNewsBanner: (state, action: PayloadAction<boolean>) => {
      state.showNewsBanner = action.payload
    },
    setSmartDashCampaignMode: (
      state,
      action: PayloadAction<SmartDashCampaignMode>,
    ) => {
      state.smartDashCampaignMode = action.payload
    },
    setBodyBuildToggled: (state, action: PayloadAction<boolean>) => {
      state.bodyBuildToggled = action.payload
    },
    setUnavailableImageFrames: (
      state,
      action: PayloadAction<ImageSeriesFrame[]>,
    ) => {
      state.unavailableImageFrames = action.payload
    },
    setUserSelectionTimestamp: (
      state,
      action: PayloadAction<number | null>,
    ) => {
      state.userSelectionTimestampReduxCompatible = action.payload
    },
    setGuidedViewPage: (
      state,
      action: PayloadAction<GuidedViewPageType | null>,
    ) => {
      state.guidedViewPage = action.payload
    },
    setSummaryOrRfqOpenedFromPage: (
      state,
      action: PayloadAction<GuidedViewPageType | null>,
    ) => {
      state.summaryOrRfqOpenedFromPage = action.payload
    },
    setGuidedViewLoadTime: (state, action: PayloadAction<number>) => {
      state.guidedViewLoadTime = action.payload
    },
    setGuidedViewFirstTimeAllView: (state, action: PayloadAction<boolean>) => {
      state.guidedViewFirstTimeAllView = action.payload
    },
    setGuidedViewPageDescriptionHidden: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.guidedViewPageDescriptionHidden = action.payload
    },
  },
})

export const {
  deleteTruckFromGarageList,
  setRfqInput,
  setRfqOrEysButtonEnabled,
  setError,
  setExtendedCheckState,
  setGarageList,
  setGuidedOfferingState,
  setGuidedOfferingStateStatus,
  setInitState,
  loggedInStatus,
  setPendingUserTrackingPageNavigation,
  pushPageViewTrackingEvent,
  setSessionId,
  startHideInitialLoadingScreen,
  setLoadingScreenIsHidden,
  setMarketDenomination,
  setSeriesFrame,
  setIsLoadedTruck,
  setShowFullPageViewer,
  setShowPanoramaViewer,
  setShowNewsBanner,
  setSmartDashCampaignMode,
  setBodyBuildToggled,
  setUnavailableImageFrames,
  setUserSelectionTimestamp,
  setGuidedViewPage,
  setSummaryOrRfqOpenedFromPage,
  setGuidedViewLoadTime,
  setGuidedViewFirstTimeAllView,
  setGuidedViewPageDescriptionHidden,
} = sessionDataSlice.actions

export const getSessionState = (state: RootState) => state.sessionData
export const getErrorState = (state: RootState) => state.sessionData.error
export const getGarageListState = (state: RootState) =>
  state.sessionData.garage.list
export const getIsLoggedInState = (state: RootState) =>
  state.sessionData.account.isloggedin
export const getPreloaderState = (state: RootState) => state.sessionData.preload
export const getRfqInput = (state: RootState) => state.sessionData.rfqInput
export const getRfqOrEysButtonEnabled = (state: RootState) =>
  state.sessionData.rfqOrEysButtonEnabled
export const getSessionIdState = (state: RootState) =>
  state.sessionData.sessionId
export const getSessionInitDataState = (state: RootState) =>
  state.sessionData.startupDataBundle?.sessionInitData
export const getStartupData = (state: RootState) =>
  state.sessionData.startupDataBundle
export const getGuidedOfferingClientState = (state: RootState) =>
  state.sessionData.guidedOfferingState
export const getGoEtelLastUpdatedStateRaw = (state: RootState) =>
  state.sessionData.startupDataBundle?.sessionInitData
    .secmLastUpdatedTimeAsNumber
export const getExtendedCheckStatus = (state: RootState) =>
  state.sessionData.extendedCheckState
export const getMarketDenomination = (state: RootState) =>
  state.sessionData.marketDenomination
export const getSeriesFrame = (state: RootState) =>
  state.sessionData.seriesFrame
export const getIsLoadedTruck = (state: RootState) =>
  state.sessionData.isLoadedTruck
export const getBodyBuildToggled = (state: RootState) =>
  state.sessionData.bodyBuildToggled
export const getUnavailableImageFrames = (state: RootState) =>
  state.sessionData.unavailableImageFrames
export const getSmartDashCampaignMode = (state: RootState) =>
  state.sessionData.smartDashCampaignMode
const getUserSelectionTimestampRaw = (state: RootState) =>
  state.sessionData.userSelectionTimestampReduxCompatible
export const getGuidedViewPage = (state: RootState) =>
  state.sessionData.guidedViewPage
export const getSummaryOrRfqOpenedFromPage = (state: RootState) =>
  state.sessionData.summaryOrRfqOpenedFromPage
export const getGuidedViewLoadTime = (state: RootState) =>
  state.sessionData.guidedViewLoadTime
export const getGuidedViewFirstTimeAllView = (state: RootState) =>
  state.sessionData.guidedViewFirstTimeAllView
export const getGuidedViewPageDescriptionHidden = (state: RootState) =>
  state.sessionData.guidedViewPageDescriptionHidden

export default sessionDataSlice.reducer

// Memoized selector to convert the timestamp to a Date object
export const getUserSelectionTimestamp = createSelector(
  [getUserSelectionTimestampRaw],
  (timestampAsNumber) => {
    if (timestampAsNumber === null) {
      return null
    }
    const date = new Date()
    date.setTime(timestampAsNumber)
    return date
  },
)

export const getGoEtelLastUpdatedState = createSelector(
  [getGoEtelLastUpdatedStateRaw],
  (timeAsNumber) => {
    if (!timeAsNumber) {
      return null
    }
    const date = new Date()
    date.setTime(timeAsNumber)
    return date
  },
)
